import { ALL_MODULE_CONFIG_QUERYS_SHALLOW, Module } from '@/modules/companies'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { ComboboxItem } from '../combobox'
import { Combobox } from '../combobox'
import type { ModuleComboboxProps } from './types'

const LABEL_SORT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

export function ModuleCombobox({
  mode,
  value,

  include,
  exclude = [],
  onlyActive = true,
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: ModuleComboboxProps) {
  const translations = useTranslate({
    placeholder: 'settings.select-module',
    module: {
      activities: 'common.activities',
      absences: 'common.absences',
      issues: 'common.issues',
      infringements: 'common.infringements',
      salary: 'common.salary',
      forum: 'common.forum',
      terminal: 'common.terminal',
      timesheets: 'common.timesheets',
      vehicles: 'common.vehicles',
      handbook: 'common.handbook',
      routePlanner: 'common.route-planner',
      fleet: 'vehicles.fleet',
      planner: 'common.planner',
    },
    searchPlaceholder: 'components.combobox.search-placeholder',
    noResults: 'components.combobox.no-results',
  })

  const { data: moduleConfigsData } = useQuery(ALL_MODULE_CONFIG_QUERYS_SHALLOW)

  const moduleLabelMap = useMemo<Record<string, string>>(
    () => ({
      [Module.ACTIVITIES]: translations.module.activities,
      [Module.ABSENCES]: translations.module.absences,
      [Module.ISSUES]: translations.module.issues,
      [Module.INFRINGEMENTS]: translations.module.infringements,
      [Module.SALARY]: translations.module.salary,
      [Module.FORUM]: translations.module.forum,
      [Module.TERMINAL]: translations.module.terminal,
      [Module.TIMESHEETS]: translations.module.timesheets,
      [Module.VEHICLES]: translations.module.vehicles,
      [Module.HANDBOOK]: translations.module.handbook,
      [Module.ROUTEPLANNER]: translations.module.routePlanner,
      [Module.FLEET]: translations.module.fleet,
      [Module.PLANNER]: translations.module.planner,
    }),
    [translations.module]
  )

  const moduleItems: ComboboxItem[] = useMemo(() => {
    const modules = moduleConfigsData?.allModuleConfigs ?? []
    const hasExcludeFilter = exclude.length > 0
    const hasIncludeFilter = !!include?.length
    const excludeSet = hasExcludeFilter ? new Set(exclude) : null
    const includeSet = hasIncludeFilter ? new Set(include) : null

    const items: ComboboxItem[] = []

    for (const moduleConfig of modules) {
      if (hasExcludeFilter && excludeSet?.has(moduleConfig.id)) continue
      if (onlyActive && !moduleConfig.active) continue
      if (hasIncludeFilter && !includeSet?.has(moduleConfig.name)) continue

      const label = moduleLabelMap[moduleConfig.name]

      if (!label) continue

      items.push({
        value: moduleConfig.id,
        label,
        filterValue: label,
      })
    }

    return items.sort((a, b) => {
      if (typeof a.label !== 'string' || typeof b.label !== 'string') return 0
      return LABEL_SORT_COLLATOR.compare(a.label, b.label)
    })
  }, [
    moduleConfigsData?.allModuleConfigs,
    exclude,
    onlyActive,
    include,
    moduleLabelMap,
  ])

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={moduleItems}
        clearable={clearable}
        placeholder={placeholder ?? translations.placeholder}
        searchPlaceholder={searchPlaceholder ?? translations.searchPlaceholder}
        noResultsText={noResultsText ?? translations.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={moduleItems}
      placeholder={placeholder ?? translations.placeholder}
      searchPlaceholder={searchPlaceholder ?? translations.searchPlaceholder}
      noResultsText={noResultsText ?? translations.noResults}
      onChange={onChange}
    />
  )
}
